import type { KeyValueStore } from '../core';
import { sfx } from '../audio/sfx';
import { browserStore } from './loadoutStore';

const KEY = 'critter.sound';

export interface SoundChannels {
  music: boolean;
  sfx: boolean;
  voice: boolean;
}

export function parseSoundChannels(raw: string | null): SoundChannels {
  if (!raw) return { music: true, sfx: true, voice: true };
  try {
    const data = JSON.parse(raw) as Partial<SoundChannels>;
    return {
      music: data.music !== false,
      sfx: data.sfx !== false,
      voice: data.voice !== false,
    };
  } catch {
    return { music: true, sfx: true, voice: true };
  }
}

export function loadSoundChannels(store: KeyValueStore = browserStore): SoundChannels {
  return parseSoundChannels(store.getItem(KEY));
}

export function saveSoundChannels(channels: SoundChannels, store: KeyValueStore = browserStore): void {
  store.setItem(KEY, JSON.stringify(channels));
}

/** What the pause sheet should paint. A master mute shows every bus as off. */
export function effectiveSoundChannels(): SoundChannels {
  if (sfx.muted) return { music: false, sfx: false, voice: false };
  return { music: sfx.musicOn, sfx: sfx.effectsOn, voice: sfx.voiceOn };
}

export function applySoundChannels(channels: SoundChannels): void {
  sfx.setMusic(channels.music);
  sfx.setEffects(channels.sfx);
  sfx.setVoice(channels.voice);
}

/**
 * A tap while the master mute is down lifts that mute and turns this bus on,
 * leaving the other saved buses as they were.
 */
export function toggleSoundChannel(
  key: keyof SoundChannels,
  store: KeyValueStore = browserStore,
): SoundChannels {
  const saved = loadSoundChannels(store);
  const next = sfx.muted ? { ...saved, [key]: true } : { ...saved, [key]: !saved[key] };
  saveSoundChannels(next, store);
  applySoundChannels(next);
  return effectiveSoundChannels();
}

/** Restore the buses before the first scene asks the lobby to play. */
export function installSoundPrefs(store: KeyValueStore = browserStore): void {
  const channels = loadSoundChannels(store);
  sfx.musicOn = channels.music;
  sfx.effectsOn = channels.sfx;
  sfx.voiceOn = channels.voice;
}
